import ErrorIcon from '@mui/icons-material/Error';
import Visibility from '@mui/icons-material/Visibility';
import VisibilityOff from '@mui/icons-material/VisibilityOff';
import { FormControl, FormHelperText, IconButton, InputAdornment, TextField, useTheme, Button } from '@mui/material';
import pino from 'pino';
import { lazy, Suspense, useEffect, useState } from 'react';
import type { FC, FormEvent, PropsWithChildren } from 'react';
import { useLoaderData, useLocation, useNavigate, useParams } from 'react-router';
import { useEffectOnce } from 'react-use';
import { z } from 'zod';

import { notificationStrings, sessionStrings } from '../../../../constants/strings';
import { UI_BUTTON_PRIMARY } from '../../../../constants/uiConstants';
import { userKeys } from '../../../../queries/query-keys';
import { useActivateUserQuery, useSessionLogoutQuery } from '../../../../queries/sessionQuery';
import type { ActivateUser } from '../../../../repositories/sessionRepository';
import UserRepository from '../../../../repositories/userRepository';
import type { registerActivateLoader } from '../../../../router/loaders/signin/registerActivateLoader';
import { queryClient } from '../../../../router/Root';
import { useSigninMessageNavigation } from '../../../../router/routerUtils';
import { isSupportAdmin, isCompanyOrSupportAdmin } from '../../../../selectors/userSelectors';
import { useZustand } from '../../../../zustandStore';
import { passwordRequireMessage, type MFARegister } from '../hsbc/steps/defaultRegistration/step_3/passwordSetupForm';
import { usePasswordInput } from '../loginform/hooks/usePasswordInput';

const SetupTwoFactorAuth = lazy(() => import('./setupTwoFactorAuth'));

const logger = pino();

export const passwordRegex =
	/^(?=.*[A-Z])(?=.*\d)(?=.*[!#$%&()*+,-./:;<=>?@[\\\]^_`{|}~])[\d!#$%&()*+,-./:;<=>?@[\\\]^_`{|}~A-Za-z]{10,}$/;

const activateUserSchema = z
	.object({
		username: z.email(),
		firstName: z.string().min(1),
		lastName: z.string().min(1),
		password: z.string().check(ctx => {
			if (ctx.value.length < 10) {
				ctx.issues.push({
					code: 'custom',
					message: passwordRequireMessage,
					input: ctx.value,
				});
			} else if (!passwordRegex.test(ctx.value)) {
				ctx.issues.push({
					code: 'custom',
					message: passwordRequireMessage,
					input: ctx.value,
				});
			}
		}),
		confirmPassword: z.string(),
	})
	.check(ctx => {
		if (ctx.value.confirmPassword !== ctx.value.password) {
			ctx.issues.push({
				code: 'custom',
				message: 'The passwords did not match',
				input: ctx.value,
			});
		}
	});

const ActivationForm: FC<PropsWithChildren> = () => {
	const {
		invitation,

		setInvitationFirstName,
		setInvitationUserName,
		setInvitationLastName,
		setInvitationFormLoading,
		resetInvitationForm,
	} = useZustand(state => ({ ...state }));
	const { username, firstName, lastName, isFormLoading } = invitation;
	const {
		state: passwordState,
		handlePasswordChange,
		handlePasswordValidation,
		handleConfirmPasswordValidation,
		resetPasswordFormState,
	} = usePasswordInput();
	const [showPassword, setShowPassword] = useState(false);
	const [showConfirmPassword, setShowConfirmPassword] = useState(false);
	const { userId, activationToken } = useParams();
	const activateUser = useActivateUserQuery<ActivateUser, never>();
	const navigate = useNavigate();
	const { setDialog, closeDialog, setMfaData, setShowTwoFactorAuth, setNotification, registration } = useZustand();
	const { showTwoFactorAuth } = registration;
	const location = useLocation();
	const initialData = useLoaderData() as Awaited<ReturnType<typeof registerActivateLoader>>;
	const { data: userData, error } = initialData;
	const logout = useSessionLogoutQuery();
	const theme = useTheme();
	const isInvitation = !location.pathname.includes('register');
	const isFormValid = activateUserSchema.safeParse({
		username,
		firstName,
		lastName,
		password: passwordState.passwordValue,
		confirmPassword: passwordState.confirmPasswordValue,
	}).success;
	const { navigateToSigninMessage } = useSigninMessageNavigation();

	const onActivateUser = async () => {
		const user = await UserRepository.getUser();
		queryClient.setQueryData([userKeys.getUser], user);
		const isTsAdmin = isSupportAdmin(user?.roles);

		if (user && !isTsAdmin && !isCompanyOrSupportAdmin(user?.roles) && user?.roles.length === 0) {
			logout.mutate();
			setNotification([
				{
					type: notificationStrings.ERROR,
					message: 'Your account has been disabled, please contact your company or team administrator.',
				},
			]);
		} else if (isTsAdmin) {
			void navigate('/settings/overview');
		} else {
			if (isCompanyOrSupportAdmin(user?.roles || [])) {
				void navigate('/wallet');
			} else {
				void navigate('/wallet/my-cards');
			}
		}
	};

	const handleSubmit = (event: FormEvent<HTMLFormElement>) => {
		event.preventDefault();
		setInvitationFormLoading(true);
		if (!userData) {
			logger.error('User data is missing');
			return;
		}
		const options = {
			activationToken: activationToken || '',
			companyName: userData?.companyName,
			email: userData?.username,
			username: userData?.username,
			firstName, // should get firstName & lastName from zustand instead of userData
			lastName,
			password: passwordState.passwordValue,
			userId: userId || '',
		};
		activateUser.mutate(options, {
			onSuccess: async (data: MFARegister) => {
				setMfaData(data);
				if (data?.mfaOtp?.status === 'setup_required') {
					setShowTwoFactorAuth(true);
				} else {
					setShowTwoFactorAuth(false);
					await onActivateUser();
				}
				resetPasswordFormState();
				resetInvitationForm();
			},
			onError: () => {
				setInvitationFormLoading(false);

				setDialog({
					type: notificationStrings.WARNING,
					message: 'Oh snap! We were not able to create your account.',
					open: true,
					actionText: 'TRY AGAIN',
					showCancel: false,
					onAccept: closeDialog,
				});
			},
			onSettled: () => {
				setInvitationFormLoading(false);
			},
		});
	};

	useEffectOnce(() => {
		if (!error) {
			return;
		}
		navigateToSigninMessage({
			heading: 'Sorry, we are having technical difficulties. Please try again later.',
			message:
				'The link to activate your account is invalid or has expired. Please check with the person who invited you to get another invitation.',
		});
	});

	useEffect(() => {
		if (userData) {
			if (userData.firstName) {
				setInvitationFirstName(userData.firstName);
			}
			if (userData.lastName) {
				setInvitationLastName(userData.lastName);
			}
			if (userData.username) {
				setInvitationUserName(userData.username);
			}
		}
	}, [userData]);

	return (
		<>
			{showTwoFactorAuth ? (
				<Suspense fallback={<div />} key={location.state?.key}>
					<SetupTwoFactorAuth />
				</Suspense>
			) : (
				<form
					data-testid="form"
					style={{
						padding: `0 ${theme.tsGoUnit.tsUnit} ${theme.tsGoUnit.tsUnit}`,
						display: 'flex',
						flexDirection: 'column',
						alignItems: 'center',
						gap: theme.tsGoUnit.tsUnit,
						marginBottom: 0,
					}}
					autoComplete="off"
					onSubmit={handleSubmit}
				>
					{isInvitation && (
						<>
							<FormControl style={{ padding: 0 }} fullWidth variant="outlined">
								<TextField
									label="Username"
									style={{ padding: 0, margin: 0 }}
									slotProps={{
										inputLabel: {
											style: { fontWeight: theme.typography.tsFontWeight.default },
										},
									}}
									type="text"
									required
									fullWidth
									margin="normal"
									variant="outlined"
									autoComplete="off"
									name="USER_NAME"
									value={username}
									disabled
								/>
							</FormControl>
							<FormControl style={{ padding: 0 }} fullWidth variant="outlined">
								<TextField
									label="First Name"
									style={{ padding: 0, margin: 0 }}
									slotProps={{
										inputLabel: {
											style: { fontWeight: theme.typography.tsFontWeight.default },
										},
									}}
									type="text"
									required
									fullWidth
									margin="normal"
									variant="outlined"
									autoComplete="off"
									name="FIRST_NAME"
									value={firstName}
									onChange={evt => {
										setInvitationFirstName(evt.target.value);
									}}
								/>
							</FormControl>
							<FormControl style={{ padding: 0 }} fullWidth variant="outlined">
								<TextField
									label="Last Name"
									style={{ padding: 0, margin: 0 }}
									slotProps={{
										inputLabel: {
											style: { fontWeight: theme.typography.tsFontWeight.default },
										},
									}}
									type="text"
									required
									fullWidth
									margin="normal"
									variant="outlined"
									autoComplete="off"
									name="LAST_NAME"
									value={lastName}
									onChange={evt => {
										setInvitationLastName(evt.target.value);
									}}
								/>
							</FormControl>
						</>
					)}
					<FormControl style={{ padding: 0 }} fullWidth variant="outlined" error={passwordState.isPasswordError}>
						<TextField
							name="password"
							label="Password"
							style={{ padding: 0, margin: 0 }}
							type={showPassword ? 'text' : 'password'}
							required
							fullWidth
							margin="normal"
							variant="outlined"
							autoComplete="off"
							onChange={handlePasswordChange}
							onBlur={handlePasswordValidation}
							value={passwordState.passwordValue}
							error={passwordState.isPasswordError}
							slotProps={{
								htmlInput: {
									'data-testid': 'password',
								},

								inputLabel: {
									style: { fontWeight: theme.typography.tsFontWeight.default },
								},
								input: {
									endAdornment:
										passwordState.passwordValue?.length > 0 ? (
											<InputAdornment position="end">
												<IconButton onClick={() => setShowPassword(prev => !prev)} edge="end">
													{showPassword ? <Visibility fontSize="small" /> : <VisibilityOff fontSize="small" />}
												</IconButton>
											</InputAdornment>
										) : null,
								},
							}}
						/>
						{passwordState.isPasswordError && (
							<FormHelperText style={{ marginLeft: 0 }}>
								<ErrorIcon fontSize="small" style={{ verticalAlign: 'middle' }} />{' '}
								{'Must be at least 10 characters, at least one capital letter, one number, and one special character'}
							</FormHelperText>
						)}
					</FormControl>
					<FormControl style={{ padding: 0 }} fullWidth variant="outlined" error={passwordState.isConfirmPasswordError}>
						<TextField
							name="confirm password"
							label={sessionStrings.CONFIRM_PASSWORD}
							style={{ padding: 0, margin: 0 }}
							type={showConfirmPassword ? 'text' : 'password'}
							required
							fullWidth
							margin="normal"
							variant="outlined"
							autoComplete="off"
							onChange={handlePasswordChange}
							onBlur={handleConfirmPasswordValidation}
							value={passwordState.confirmPasswordValue}
							error={passwordState.isConfirmPasswordError}
							slotProps={{
								htmlInput: {
									'data-testid': 'confirm-password',
								},

								inputLabel: {
									style: { fontWeight: theme.typography.tsFontWeight.default },
								},
								input: {
									endAdornment:
										passwordState.confirmPasswordValue?.length > 0 ? (
											<InputAdornment position="end">
												<IconButton onClick={() => setShowConfirmPassword(prev => !prev)} edge="end">
													{showConfirmPassword ? <Visibility fontSize="small" /> : <VisibilityOff fontSize="small" />}
												</IconButton>
											</InputAdornment>
										) : null,
								},
							}}
						/>
						{passwordState.isConfirmPasswordError && (
							<FormHelperText style={{ marginLeft: 0 }}>
								<ErrorIcon fontSize="small" style={{ verticalAlign: 'middle' }} />{' '}
								{passwordState.confirmPasswordValue === passwordState.passwordValue
									? 'Must be at least 10 characters, at least one capital letter, one number, and one special character'
									: 'The passwords did not match.'}
							</FormHelperText>
						)}
					</FormControl>
					<Button
						style={{
							width: '100%',
							fontSize: theme.typography.pxToRem(theme.typography.tsFontSize.default),
						}}
						data-testid="submit"
						color={UI_BUTTON_PRIMARY}
						disabled={!isFormValid}
						type="submit"
						loading={isFormLoading}
						startIcon={<></>}
						variant="contained"
					>
						{'Next'}
					</Button>
				</form>
			)}
		</>
	);
};

export default ActivationForm;
